import React, { useMemo, useState } from 'react';
import { Line } from '../types';
import { LINE_COLUMNS } from '../constants/columns';
import EditableCell from './EditableCell';
import ExcelFilter from './ExcelFilter';
import { useSubinventoryLov } from '../hooks/useSubinventoryLov';
import { useLocatorLov } from '../hooks/useLocatorLov';

interface LinesTableProps {
  lines: Line[];
  isDarkTheme?: boolean;
  onLineUpdate?: (lineId: string, field: string, value: string) => void;
  selectedHeaderToOrg?: string;
  newRowIds?: Set<string>;
  scannedLineIds?: Set<string>;
  clearErrorsRef?: React.MutableRefObject<(() => void) | null>;
}

const LinesTable: React.FC<LinesTableProps> = ({ lines, isDarkTheme, onLineUpdate, selectedHeaderToOrg, newRowIds = new Set(), scannedLineIds = new Set(), clearErrorsRef }) => {
  const [filters, setFilters] = useState<Record<string, string[]>>({
    'Serial Number': []
  });
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, Record<string, string>>>({});
  const { data: subinventories, refetch: refetchSubinventories } = useSubinventoryLov(selectedHeaderToOrg, false);
  const { refetch: refetchLocators } = useLocatorLov(selectedHeaderToOrg, '', false);

  // Expose clear errors function to parent
  React.useEffect(() => {
    if (clearErrorsRef) {
      clearErrorsRef.current = () => setFieldErrors({});
    }
  }, [clearErrorsRef]);

  // Clear field errors when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('table') && !target.closest('input') && !target.closest('select')) {
        setFieldErrors({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  React.useEffect(() => {
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      lines.forEach(line => {
        if (newErrors[line.id]) {
          Object.keys(newErrors[line.id]).forEach(field => {
            const fieldMap: Record<string, keyof Line> = {
              'Validated Serial Number': 'validatedSerialNumber',
              'To Subinventory': 'toSubinventory',
              'Repair Eligibility': 'repairEligibility'
            };
            const fieldKey = fieldMap[field];
            if (fieldKey && line[fieldKey]) {
              newErrors[line.id][field] = '';
            }
          });
        }
      });
      return newErrors;
    });
  }, [lines]);

  const isLineEditable = (line: Line): boolean => {
    return line.status === 'New' || line.status === '' || !line.status;
  };

  const validateMandatoryField = (lineId: string, column: string, value: string): string => {
    const mandatoryFields = ['Validated Serial Number', 'To Subinventory', 'Repair Eligibility'];
    if (mandatoryFields.includes(column) && (!value || value.trim() === '')) {
      return `${column} is mandatory and cannot be null`;
    }
    return '';
  };

  const handleLineUpdate = (lineId: string, field: string, value: string) => {
    // Clear error when user updates field
    if (fieldErrors[lineId]?.[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [lineId]: {
          ...prev[lineId],
          [field]: ''
        }
      }));
    }
    
    // Validate mandatory fields on blur/change
    const error = validateMandatoryField(lineId, field, value);
    if (error) {
      setFieldErrors(prev => ({
        ...prev,
        [lineId]: {
          ...prev[lineId] || {},
          [field]: error
        }
      }));
    }
    
    if (onLineUpdate) {
      onLineUpdate(lineId, field, value);
    }
  };

  const filteredLines = useMemo(() => {
    return lines.filter(line => {
      const serialNumberMatch = filters['Serial Number'].length === 0 || 
        filters['Serial Number'].includes(line.serialNumber);
      
      return serialNumberMatch;
    });
  }, [lines, filters]);

  const handleFilterChange = (column: string, selectedValues: string[]) => {
    setFilters(prev => ({
      ...prev,
      [column]: selectedValues
    }));
  };

  const getColumnValues = (column: string): string[] => {
    if (column === 'Serial Number') {
      return lines.map(line => line.serialNumber || '');
    }
    return [];
  };
  
  const getLineValue = (line: Line, column: string): string => {
    const fieldMap: Record<string, keyof Line> = {
      'Description': 'description',
      'Serial Number': 'serialNumber',
      'Asset Number': 'assetNumber',
      'Validated Serial Number': 'validatedSerialNumber',
      'Validated Asset Number': 'validatedAssetNumber',
      'Item Number': 'itemNumber',
      'To Subinventory': 'toSubinventory',
      'To Locator': 'toLocator',
      'Validated Serial Item': 'validatedSerialItem',
      'Status': 'status',
      'Tracking Number': 'trackingNumber',
      'Internal Sales Order': 'internalSalesOrder',
      'ISO Line Number': 'isoLineNumber',
      'Replacement order line status': 'replacementOrderLineStatus',
      'Repair Eligibility': 'repairEligibility'
    };
    const value = line[fieldMap[column]];
    return typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value || '');
  };

  const getStatusBackgroundColor = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'success') return '#d4edda';
    if (statusLower === 'error') return '#f8d7da';
    return 'transparent';
  };

  const getStatusTextColor = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'success') return '#155724';
    if (statusLower === 'error') return '#721c24';
    return isDarkTheme ? '#fff' : '#000';
  };

  return (
    <>
      <style>
        {`
          .thick-scrollbar::-webkit-scrollbar {
            width: 10px;
          }
          .thick-scrollbar::-webkit-scrollbar-track {
            background: ${isDarkTheme ? '#2a2a2a' : '#f1f1f1'};
          }
          .thick-scrollbar::-webkit-scrollbar-thumb {
            background: ${isDarkTheme ? '#555' : '#888'};
            border-radius: 8px;
          }
          .thick-scrollbar::-webkit-scrollbar-thumb:hover {
            background: ${isDarkTheme ? '#777' : '#555'};
          }
        `}
      </style>
    <div 
      style={{ 
        maxHeight: '375px', 
        overflowY: 'auto', 
        border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`
      }}
      className="thick-scrollbar"
    >
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: isDarkTheme ? '#444' : '#f5f5f5' }}>
            {LINE_COLUMNS.map((column) => {
              const isMandatory = ['Validated Serial Number', 'To Subinventory', 'Repair Eligibility'].includes(column);
              const hasFilter = column === 'Serial Number';
              return (
                <th key={column} style={{ 
                  padding: '4px', 
                  border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`, 
                  textAlign: 'left', 
                  color: isDarkTheme ? '#fff' : '#000', 
                  position: 'sticky', 
                  top: 0, 
                  backgroundColor: isDarkTheme ? '#444' : '#f5f5f5', 
                  fontSize: '13px', 
                  zIndex: 10,
                  minWidth: column === 'To Locator' ? '200px' : 'auto',
                  width: column === 'To Locator' ? '200px' : 'auto'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>
                      {column}
                      {isMandatory && <span style={{ color: 'black', marginLeft: '2px' }}>*</span>}
                    </span>
                    {hasFilter && (
                      <ExcelFilter
                        column={column}
                        values={getColumnValues(column)}
                        selectedValues={filters[column]}
                        onFilterChange={handleFilterChange}
                        isDarkTheme={isDarkTheme}
                      />
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {filteredLines.length === 0 ? (
            <tr style={{ backgroundColor: isDarkTheme ? '#2a2a2a' : 'white', color: isDarkTheme ? '#fff' : '#000', minHeight: '40px' }}>
              {LINE_COLUMNS.map((column) => (
                <td key={column} style={{ 
                  padding: '4px', 
                  border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`, 
                  color: isDarkTheme ? '#fff' : '#000', 
                  fontSize: '12px', 
                  minHeight: '40px',
                  minWidth: column === 'To Locator' ? '200px' : 'auto',
                  width: column === 'To Locator' ? '200px' : 'auto'
                }}>
                  
                </td>
              ))}
            </tr>
          ) : (
            filteredLines.map((line) => (
              <tr key={line.id} style={{ 
                backgroundColor: scannedLineIds.has(line.id)
                  ? (isDarkTheme ? '#0d47a1' : '#e3f2fd') 
                  : (isDarkTheme ? '#2a2a2a' : 'white'), 
                color: isDarkTheme ? '#fff' : '#000' 
              }}>
                {LINE_COLUMNS.map((column) => {
                  const cellValue = getLineValue(line, column);
                  const isEditable = isLineEditable(line) && ['Validated Serial Number', 'Validated Asset Number', 'To Subinventory', 'To Locator', 'Repair Eligibility', 'Tracking Number'].includes(column);
                  const isMandatory = ['Validated Serial Number', 'To Subinventory', 'Repair Eligibility'].includes(column);
                  const hasError = fieldErrors[line.id]?.[column];
                  const isMandatoryEmpty = isMandatory && isLineEditable(line) && (!cellValue || cellValue.trim() === '');
                  
                  return (
                    <td key={column} style={{ 
                      padding: '4px', 
                      border: `2px solid ${hasError ? '#dc3545' : (isDarkTheme ? '#555' : '#ccc')}`, 
                      backgroundColor: column === 'Status' && cellValue ? getStatusBackgroundColor(cellValue) : 'inherit',
                      color: column === 'Status' ? getStatusTextColor(cellValue) : (isDarkTheme ? '#fff' : '#000'), 
                      fontSize: '12px',
                      position: 'relative',
                      minWidth: column === 'To Locator' ? '200px' : 'auto',
                      width: column === 'To Locator' ? '200px' : 'auto',
                      wordWrap: column === 'To Locator' ? 'break-word' : 'normal'
                    }}>
                      {isEditable ? (
                        <div>
                          <EditableCell
                            key={`${line.id}-${column}`}
                            column={column}
                            value={cellValue}
                            lineId={line.id}
                            line={line}
                            onUpdate={handleLineUpdate}
                            isDarkTheme={isDarkTheme}
                            selectedHeaderToOrg={selectedHeaderToOrg}
                            subinventories={subinventories}
                            refetchSubinventories={refetchSubinventories}
                            refetchLocators={refetchLocators}
                          />
                          {hasError && (
                            <div style={{
                              color: '#dc3545',
                              fontSize: '10px',
                              marginTop: '2px',
                              lineHeight: '1.2'
                            }}>
                              {hasError}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ minHeight: '16px' }}>
                          {cellValue}
                          {isMandatoryEmpty && (
                            <div style={{
                              color: '#dc3545',
                              fontSize: '10px',
                              marginTop: '2px',
                              lineHeight: '1.2'
                            }}>
                              {column} is mandatory and cannot be null
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
    </>
  );
};

export default LinesTable;